import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { UsersAllRolesSchema } from '@iconik-io/iconik-api/src/services/users';
import { isRole, type UserRoles } from '@iconik-io/shared-lib/src/utils/roles';

import { emptyUntilLoaded } from '../../store/empty_slice_state';
import type { RolesAppState } from './types';

const initialState: RolesAppState = {
  roles: [],
  userRoles: emptyUntilLoaded<UserRoles>(),
  standardUsersRoles: [],
  browseOnlyUsersRoles: [],
};

export const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    setAllRoles: (
      state,
      action: PayloadAction<Partial<UsersAllRolesSchema>>,
    ) => {
      const {
        objects: roles,
        standard: standardUsersRoles,
        browse_only: browseOnlyUsersRoles,
      } = action.payload;

      // A request cut short by a page navigation still settles as a 200 with
      // an empty body, so every field reads back as undefined. The lists must
      // stay arrays: every private shell reads `roles.length` on each render.
      // A role the frontend does not know is dropped, since nothing can read it.
      state.roles = (roles ?? []).filter(isRole);
      state.standardUsersRoles = (standardUsersRoles ?? []).filter(isRole);
      state.browseOnlyUsersRoles = (browseOnlyUsersRoles ?? []).filter(isRole);
    },
    setUserRoles: (state, action: PayloadAction<Partial<UserRoles>>) => {
      state.userRoles = { ...state.userRoles, ...action.payload };
    },
  },
});

export const { setAllRoles, setUserRoles } = rolesSlice.actions;

export const rolesReducer = rolesSlice.reducer;
